/**
 * @file Hook d'amorçage du choix de salle du flow de réservation.
 * @module packages.common.components.table-reservation.hooks.useRoomSelection
 *
 * Lorsque le restaurant a activé le choix de salle (`config.allowRoomSelection`),
 * charge une fois la liste des salles réservables (sans contexte de date) pour
 * décider si l'étape ROOM doit exister : elle n'a de sens qu'avec au moins deux
 * salles à départager. La disponibilité par salle pour la date choisie est, elle,
 * recalculée dans `RoomStep` (où la date est connue).
 *
 * Découpler « l'étape existe-t-elle » (nombre de salles, connu tôt) de « quelles
 * salles sont libres ce jour » (par date) évite une condition de course : sans ça,
 * cliquer « Suivant » sur la date avant la fin du fetch sauterait l'étape salle.
 */
import {useEffect} from 'react';
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {logger} from 'packages/common/utils/clientLogger';

/**
 * Amorce l'état du choix de salle dans le formulaire de réservation.
 *
 * @param {Object} config - Configuration du restaurant (lit `allowRoomSelection`)
 * @param {Function} updateFormData - Mutateur du formulaire (field, value)
 * @return {void}
 */
export function useRoomSelection(config, updateFormData) {
  const {getBookableRooms} = useReservationData();

  useEffect(() => {
    // Fonctionnalité désactivée côté restaurant, ou adapter hôte n'exposant pas
    // l'opération (rétrocompat) : aucune étape de salle, rien à charger.
    if (!config?.allowRoomSelection || typeof getBookableRooms !== 'function') {
      return undefined;
    }

    // Pas de garde par ref persistante ici : sous StrictMode (dev), l'effet est
    // monté deux fois et une ref survivrait au remontage — le 1er fetch serait
    // annulé et le 2nd jamais relancé, laissant l'état vide. Les dépendances étant
    // stables (adapter mémoïsé, updateFormData en useCallback), l'effet ne tourne
    // qu'une fois en production de toute façon.
    let cancelled = false;

    (async () => {
      try {
        const rooms = await getBookableRooms({});
        if (cancelled) return;

        const list = Array.isArray(rooms) ? rooms : [];
        updateFormData('availableRooms', list);
        // L'étape n'apparaît qu'avec au moins deux salles à départager (une seule
        // salle réservable revient au comportement global sans choix).
        updateFormData('roomSelectionEnabled', list.length >= 2);
      } catch (err) {
        if (cancelled) return;
        logger.error('Erreur lors du chargement des salles réservables:', err);
        updateFormData('roomSelectionEnabled', false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config?.allowRoomSelection, getBookableRooms, updateFormData]);
}
